import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmployeeTotals } from "@/components/EmployeeTotals";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  ClipboardCheck,
  Pencil,
  Printer,
  X,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const formatDateIT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatCreatedAt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Quanti giorni sono passati dalla creazione (per badge urgenza)
const daysSinceIso = (iso) => {
  if (!iso) return 0;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};

export default function DaVidimarePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rapportini", {
        params: { only_unvidimati: true },
      });
      setItems(data || []);
    } catch {
      toast.error("Impossibile caricare i rapportini da vidimare");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((r) => r.id)));
  };

  const allChecked = items.length > 0 && selectedIds.size === items.length;
  const someChecked = selectedIds.size > 0 && selectedIds.size < items.length;

  const vidimaSelezionati = async () => {
    if (selectedIds.size === 0) {
      toast.info("Seleziona almeno un rapportino");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/rapportini/vidima-bulk", {
        ids: Array.from(selectedIds),
      });
      if (data?.skipped && data.skipped.length > 0) {
        // Iter 31x: alcuni rapportini hanno nomi ambigui non risolti
        const lines = data.skipped.slice(0, 3).map((s) => {
          const parts = [];
          if (s.unresolved?.length) {
            parts.push(
              s.unresolved
                .map((u) => `${u.nome} (${u.ore_tot}h, ${u.giorno})`)
                .join(", ")
            );
          }
          if (s.pending?.length) {
            parts.push(`da confermare: ${s.pending.join(", ")}`);
          }
          return `#${s.numero}: ${parts.join(" · ")}`;
        });
        const more =
          data.skipped.length > 3 ? `\n…e altri ${data.skipped.length - 3}` : "";
        toast.warning(
          `Vidimati ${data.modified}, ma ${data.skipped.length} bloccati (nomi non riconosciuti o elementi da confermare):\n${lines.join("\n")}${more}`,
          { duration: 12000 }
        );
      } else {
        toast.success(
          `Vidimati ${data?.modified || 0} rapportini${data?.resolved_total ? ` (${data.resolved_total} nomi auto-corretti)` : ""}`
        );
      }
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Vidimazione non riuscita"
      );
    } finally {
      setBusy(false);
    }
  };

  const vidimaTutti = async () => {
    if (!window.confirm(
      `Vidimare TUTTI i ${items.length} rapportini in attesa?\n` +
      "Verranno inclusi nei totali ufficiali."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rapportini/vidima-bulk", { all: true });
      if (data?.skipped && data.skipped.length > 0) {
        const lines = data.skipped.slice(0, 3).map((s) => {
          const parts = [];
          if (s.unresolved?.length) {
            parts.push(
              s.unresolved
                .map((u) => `${u.nome} (${u.ore_tot}h, ${u.giorno})`)
                .join(", ")
            );
          }
          if (s.pending?.length) {
            parts.push(`da confermare: ${s.pending.join(", ")}`);
          }
          return `#${s.numero}: ${parts.join(" · ")}`;
        });
        const more =
          data.skipped.length > 3 ? `\n…e altri ${data.skipped.length - 3}` : "";
        toast.warning(
          `Vidimati ${data.modified}, ma ${data.skipped.length} bloccati (nomi non riconosciuti o elementi da confermare):\n${lines.join("\n")}${more}`,
          { duration: 12000 }
        );
      } else {
        toast.success(
          `Vidimati ${data?.modified || 0} rapportini${data?.resolved_total ? ` (${data.resolved_total} nomi auto-corretti)` : ""}`
        );
      }
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Vidimazione non riuscita"
      );
    } finally {
      setBusy(false);
    }
  };

  const vidimaSingolo = async (id) => {
    try {
      const { data } = await api.post(`/rapportini/${id}/vidima`);
      if (data?.resolved > 0) {
        toast.success(
          `Vidimato (${data.resolved} ${data.resolved === 1 ? "nome auto-corretto" : "nomi auto-corretti"})`
        );
      } else {
        toast.success("Vidimato");
      }
      load();
    } catch (e) {
      // Iter 31x: 422 = nomi non riconosciuti
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.unresolved) {
        const lines = detail.unresolved
          .map((u) => `• ${u.nome} (${u.ore_tot}h, ${u.giorno})`)
          .join("\n");
        toast.error(
          `Vidimazione bloccata. Le ore di questi nomi andrebbero perse:\n${lines}\n\nApri il rapportino e correggi i nomi.`,
          { duration: 15000 }
        );
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Vidimazione non riuscita"
        );
      }
    }
  };

  const eliminaRapportino = async (r) => {
    if (!window.confirm(
      `Eliminare definitivamente il rapportino #${r.numero}?\nL'operazione non è reversibile.`
    )) return;
    try {
      await api.delete(`/rapportini/${r.id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Eliminazione non riuscita"
      );
    }
  };

  // Aggregati per il riepilogo in header
  const stats = useMemo(() => {
    const byCreator = {};
    let totalOre = 0;
    let oldestDays = 0;
    for (const r of items) {
      const c = r.created_by_name || "Sconosciuto";
      byCreator[c] = (byCreator[c] || 0) + 1;
      const righe =
        (r.giorni || []).flatMap((g) => g.righe || []) || r.righe || [];
      for (const rg of righe) totalOre += Number(rg.ore_tot || 0);
      const d = daysSinceIso(r.created_at);
      if (d > oldestDays) oldestDays = d;
    }
    return {
      total: items.length,
      totalOre,
      byCreator: Object.entries(byCreator).sort((a, b) => b[1] - a[1]),
      oldestDays,
    };
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Card className="p-10">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
          <div className="font-heading font-bold text-xl mt-3">
            Accesso riservato agli amministratori
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Approvazione
          </div>
          <h1
            className="font-heading font-black text-3xl md:text-4xl tracking-tight"
            data-testid="da-vidimare-title"
          >
            Da vidimare
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Rapportini in attesa di approvazione, ordinati dal più vecchio.
            Solo dopo la vidimazione entrano nei totali ufficiali.
          </p>
        </div>

        {items.length > 0 && (
          <Button
            onClick={vidimaTutti}
            disabled={busy}
            data-testid="vidima-tutti-button"
            className="h-12 bg-amber-600 hover:bg-amber-700 text-white"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" /> Vidima tutti ({items.length})
          </Button>
        )}
      </div>

      {/* Riepilogo */}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4" data-testid="stat-da-vidimare-count">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
              In attesa
            </div>
            <div className="font-heading font-black text-3xl tracking-tight mt-1 text-amber-700">
              {stats.total}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Ore non ufficiali
            </div>
            <div className="font-heading font-black text-3xl tracking-tight mt-1">
              {stats.totalOre.toFixed(1)}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Più vecchio
            </div>
            <div className="font-heading font-black text-3xl tracking-tight mt-1">
              {stats.oldestDays} <span className="text-base font-bold">gg</span>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Autori coinvolti
            </div>
            <div className="font-heading font-black text-3xl tracking-tight mt-1">
              {stats.byCreator.length}
            </div>
            {stats.byCreator.length > 0 && (
              <div
                className="text-[11px] text-slate-500 truncate mt-1"
                title={stats.byCreator.map(([n, c]) => `${n} (${c})`).join(", ")}
              >
                {stats.byCreator
                  .slice(0, 3)
                  .map(([n, c]) => `${n.split(" ")[0]} ×${c}`)
                  .join(" · ")}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Toolbar selezione */}
      {items.length > 0 && (
        <Card
          className="p-3 md:p-4 sticky top-16 z-20 bg-white"
          data-testid="da-vidimare-toolbar"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={toggleSelectAll}
                className="h-5 w-5 accent-[#0044FF]"
                data-testid="select-all-checkbox"
              />
              <span className="text-sm font-semibold text-slate-700">
                {selectedIds.size === 0
                  ? "Seleziona tutti"
                  : `${selectedIds.size} / ${items.length} selezionati`}
              </span>
            </label>
            <div className="flex-1" />
            {selectedIds.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-10"
                onClick={() => setSelectedIds(new Set())}
                data-testid="clear-selection-button"
              >
                <X className="h-4 w-4 mr-1" /> Annulla
              </Button>
            )}
            <Button
              size="sm"
              onClick={vidimaSelezionati}
              disabled={busy || selectedIds.size === 0}
              data-testid="vidima-selezionati-button"
              className="h-10 bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Vidima selezionati
            </Button>
          </div>
        </Card>
      )}

      {/* Lista */}
      {loading ? (
        <div className="text-center text-slate-500 py-12">Caricamento…</div>
      ) : items.length === 0 ? (
        <Card
          className="p-12 text-center border-2 border-emerald-200 bg-emerald-50/40"
          data-testid="empty-state"
        >
          <ClipboardCheck className="h-12 w-12 mx-auto text-emerald-500" />
          <div className="font-heading font-black text-2xl mt-3 text-emerald-900">
            Tutto in ordine!
          </div>
          <p className="text-sm text-emerald-800 mt-2 max-w-md mx-auto">
            Non ci sono rapportini in attesa di vidimazione. I capi cantiere
            non hanno aggiunto nuovi rapportini da approvare.
          </p>
          <Link to="/lista" className="inline-block mt-5">
            <Button variant="outline" className="h-11">
              Vai alla Dashboard
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const righe =
              (r.giorni || []).flatMap((g) => g.righe || []) || r.righe || [];
            const ore = righe.reduce(
              (s, rg) => s + Number(rg.ore_tot || 0),
              0
            );
            const commesseSet = Array.from(
              new Set(righe.map((rg) => rg.commessa_codice).filter(Boolean))
            );
            const di = r.data_inizio || r.data;
            const df = r.data_fine || r.data;
            const dateLabel =
              di === df
                ? formatDateIT(di)
                : `${formatDateIT(di)} → ${formatDateIT(df)}`;
            const days = daysSinceIso(r.created_at);
            const urgent = days >= 7;
            const isSelected = selectedIds.has(r.id);
            return (
              <Card
                key={r.id}
                data-testid={`da-vidimare-card-${r.id}`}
                className={`p-3 md:p-4 transition-colors border-l-4 ${
                  isSelected
                    ? "border-l-[#0044FF] bg-blue-50/50"
                    : urgent
                    ? "border-l-red-400"
                    : "border-l-amber-400"
                }`}
              >
                <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(r.id)}
                    className="h-5 w-5 shrink-0 accent-[#0044FF] cursor-pointer"
                    data-testid={`select-${r.id}`}
                  />
                  <div className="h-12 min-w-[3rem] px-2 shrink-0 rounded-md bg-slate-900 text-white grid place-items-center">
                    <div className="text-center">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">n°</div>
                      <div className="font-heading font-black text-sm tabular-nums leading-tight">
                        {r.numero}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm capitalize truncate">
                      {dateLabel}
                    </div>
                    <div className="text-xs text-slate-600 truncate">
                      {commesseSet.length > 0
                        ? commesseSet.join(" · ")
                        : "Nessuna commessa"}
                      {" · "}
                      {ore.toFixed(1)}h
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatCreatedAt(r.created_at)}
                      </span>
                      {r.created_by_name && (
                        <span>· da {r.created_by_name}</span>
                      )}
                      {days > 0 && (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded font-bold ${
                            urgent
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {days} {days === 1 ? "giorno" : "giorni"} fa
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    <Button
                      size="sm"
                      className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => vidimaSingolo(r.id)}
                      data-testid={`vidima-${r.id}`}
                      title="Vidima singolo"
                    >
                      <CheckCircle2 className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">Vidima</span>
                    </Button>
                    <Link to={`/rapportino/${r.id}/stampa`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10"
                        title="Anteprima/Stampa"
                        data-testid={`stampa-${r.id}`}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to={`/rapportino/${r.id}?from=da-vidimare`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10"
                        title="Modifica"
                        data-testid={`modifica-${r.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => eliminaRapportino(r)}
                      data-testid={`elimina-${r.id}`}
                      title="Elimina"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Iter 46: totale ore per dipendente di questo rapportino */}
                <EmployeeTotals
                  righe={righe}
                  testId={`totali-dip-${r.id}`}
                  compact
                />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
